import { Box, HStack, Icon, Pressable, Text, VStack, cx } from '@aj/ui';
import type { IconName } from '@aj/ui';
import { useEffect, useState } from 'react';

import { useActions } from '../data/hooks.js';
import { useT } from '../providers/I18nProvider.js';
import { LogForm } from '../components/LogForm.js';
import { Compose } from './home/Compose.js';
import type { MessageKey } from '@aj/i18n';

/**
 * The centre button's menu (§47).
 *
 * The ORDER is the design, not a detail: doing first, capturing second,
 * creating last. Every competitor puts "new habit" at the top, which teaches
 * the optimizer mindset — arrange the system, never do the work — and is why
 * their users end up with fourteen habits and keep two. Here it is the small
 * row at the bottom, because a student creates a habit perhaps four times a
 * year and studies every day.
 *
 * Rows for features that do not exist yet are shown disabled rather than
 * hidden. The menu is a statement about what the app is for; hiding the two
 * *doing* rows until they are built would leave exactly the "add things" sheet
 * this design exists to avoid.
 */

type Row = {
  id: string;
  icon: IconName;
  key: MessageKey;
  /** Compose field to swap in, when the row creates something by name. */
  compose?: { labelKey: MessageKey; actionKey: MessageKey; submit: 'habit' | 'task' };
  /** Rows whose panel is a form of its own rather than a single field. */
  panel?: 'log';
  /** Rows that leave the sheet entirely — Focus takes the whole screen. */
  fullScreen?: true;
};

const DOING: Row[] = [
  { id: 'focus', icon: 'bolt', key: 'now.startFocus', fullScreen: true },
  { id: 'log', icon: 'check', key: 'now.logEntry', panel: 'log' },
];

const CAPTURING: Row[] = [
  {
    id: 'task',
    icon: 'plus',
    key: 'now.quickTask',
    compose: { labelKey: 'home.taskName', actionKey: 'home.addTask', submit: 'task' },
  },
];

const CREATING: Row[] = [
  {
    id: 'habit',
    icon: 'plus',
    key: 'now.newHabit',
    compose: { labelKey: 'home.habitName', actionKey: 'home.addHabit', submit: 'habit' },
  },
  { id: 'goal', icon: 'map', key: 'now.newGoal' },
];

export type NowSheetProps = {
  onClose: () => void;
  /** Focus takes over the whole screen, so the shell has to start it. */
  onStartFocus: () => void;
};

export function NowSheet({ onClose, onStartFocus }: NowSheetProps) {
  const t = useT();
  const actions = useActions();
  const [composing, setComposing] = useState<Row | null>(null);

  // Escape closes from anywhere in the sheet, including the compose field —
  // Compose calls onDismiss for its own Escape, which lands on the same close.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const submit = (row: Row, name: string) => {
    if (row.compose?.submit === 'habit') void actions.createHabit({ name });
    else void actions.createTask({ name });
    onClose();
  };

  const item = (row: Row, small = false) => {
    const enabled =
      row.compose !== undefined || row.panel !== undefined || row.fullScreen === true;
    return (
      <Pressable
        key={row.id}
        disabled={!enabled}
        onClick={() => (row.fullScreen ? onStartFocus() : setComposing(row))}
        className={cx(
          'flex w-full items-center gap-3 rounded-lg px-3 text-left',
          small ? 'h-11' : 'h-13',
          enabled ? 'text-ink hover:bg-sunken' : 'text-subtle',
        )}
      >
        <Icon name={row.icon} size={small ? 18 : 21} />
        <Text variant={small ? 'caption' : 'body'} className="min-w-0 flex-1 truncate text-current">
          {t(row.key)}
        </Text>
        {!enabled && (
          <Text variant="caption" tone="subtle" className="shrink-0">
            {t('now.soon')}
          </Text>
        )}
      </Pressable>
    );
  };

  return (
    <Box className="fixed inset-0 z-50 flex flex-col justify-end">
      {/* Dismiss by tapping away. Presentational only — Escape and the close
          button are the accessible routes, so this stays out of the tab order. */}
      <Box
        aria-hidden
        onClick={onClose}
        className={cx(
          'absolute inset-0 bg-ink/40 backdrop-blur-[2px]',
          'animate-[aj-scrim-in_var(--aj-duration-fast)_ease-out]',
        )}
      />

      <VStack
        role="dialog"
        aria-modal="true"
        aria-label={t('now.title')}
        gap={1}
        className={cx(
          'relative mx-auto w-full max-w-md rounded-t-2xl border border-line bg-raised',
          'px-3 pt-3 pb-6 shadow-md sm:mb-4 sm:rounded-2xl',
          // Rises from below its own edge and settles with a little
          // overshoot. `will-change` because this is the one animation that
          // runs on a phone mid-tap and a dropped frame here is felt.
          'will-change-transform',
          'animate-[aj-sheet-rise_var(--aj-duration-slow)_var(--aj-ease-spring)]',
        )}
      >
        <HStack gap={2} align="center" className="px-3 pb-1">
          <Text variant="overline" tone="subtle" className="min-w-0 flex-1">
            {t('now.title')}
          </Text>
          <Pressable
            aria-label={t('now.close')}
            onClick={onClose}
            className="grid size-8 shrink-0 place-items-center rounded-lg text-subtle hover:bg-sunken"
          >
            <Icon name="x" size={18} />
          </Pressable>
        </HStack>

        {composing?.panel === 'log' ? (
          <Box className="px-3 pt-1 pb-2">
            <LogForm onDone={onClose} />
          </Box>
        ) : composing?.compose ? (
          <Box className="px-1 pb-2">
            <Compose
              labelKey={composing.compose.labelKey}
              actionKey={composing.compose.actionKey}
              onSubmit={(name) => submit(composing, name)}
              onDismiss={onClose}
            />
          </Box>
        ) : (
          <>
            {DOING.map((row) => item(row))}
            {CAPTURING.map((row) => item(row))}
            <Box className="mx-3 my-1 h-px bg-line" />
            {CREATING.map((row) => item(row, true))}
          </>
        )}
      </VStack>
    </Box>
  );
}
